import type { OntologyApiNode } from '@/types/ontologyGraph';
import type { Gene } from '@/types/ontologyGraph';

type RawDiseaseNode = {
  identity?: number;
  labels?: string[];
  properties?: Record<string, string>;
  elementId?: string;
};

type RawGeneNode = {
  identity?: number;
  labels?: string[];
  properties?: Record<string, string>;
  elementId?: string;
};

type RawSubclassItem = {
  genes?: RawGeneNode[];
  disease: RawDiseaseNode;
  subclasses?: RawSubclassItem[];
};

type RawGraph = {
  rootDisease: RawDiseaseNode;
  subclasses: RawSubclassItem[];
};

type RawRecord = {
  graph: RawGraph;
};

type RawPayload = {
  status?: string;
  records?: RawRecord[];
};

type MockResponse = {
  status?: string;
  payload?: RawPayload;
};

/** Formato antiguo (fallback): payload.graph.nodes */
type RawNode = {
  id: number;
  labels?: string[];
  properties?: Record<string, string[] | string>;
};

type RawGraphPayloadLegacy = {
  status?: string;
  graph?: {
    nodes?: RawNode[];
    relationships?: unknown[];
  };
};

const LABEL_KEY_LEGACY = 'http__//www.w3.org/2000/01/rdf-schema#label';

export const GENE_NODE_ID_PREFIX = 'gene_';

let cachedNodes: OntologyApiNode[] | null = null;
let cachedGenesByDiseaseId: Map<string, Gene[]> | null = null;

function getFirst(value: string[] | string | undefined): string | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value[0] : undefined;
  return typeof value === 'string' ? value : undefined;
}

function diseaseToNode(disease: RawDiseaseNode, parentId: string | undefined): OntologyApiNode | null {
  const props = disease.properties ?? {};
  const id = props.id != null ? String(props.id) : undefined;
  const name = props.name ?? (id ? `Node ${id}` : undefined);
  if (!id || !name) return null;
  const cui = props.id?.match(/^C\d+$/) ? props.id : undefined;
  return {
    id,
    label: name,
    hierarchicalParent: parentId,
    hasHierarchicalChildren: false,
    numHierarchicalDescendants: 0,
    ...(cui ? { cui } : {}),
  };
}

function rawGeneToGene(raw: RawGeneNode): Gene | null {
  const props = raw.properties ?? {};
  const id = props.id != null ? String(props.id) : undefined;
  const symbol = props.symbol ?? props.fullname ?? id;
  if (!id) return null;
  return {
    id,
    symbol: symbol ?? id,
    fullName: props.fullname ?? props.description,
  };
}

function parseNewFormat(payload: RawPayload): {
  nodes: OntologyApiNode[];
  genesByDiseaseId: Map<string, Gene[]>;
} {
  const records = payload?.records ?? [];
  const genesByDiseaseId = new Map<string, Gene[]>();

  if (records.length === 0) return { nodes: [], genesByDiseaseId };

  const nodeById = new Map<string, OntologyApiNode>();
  let rootId: string | undefined;

  for (const record of records) {
    const graph = record?.graph;
    if (!graph?.rootDisease) continue;

    const root = graph.rootDisease;
    const rootProps = root.properties ?? {};
    const rid = rootProps.id != null ? String(rootProps.id) : undefined;
    const rootName = rootProps.name ?? (rid ? `Node ${rid}` : '');
    if (!rid) continue;

    if (!rootId) rootId = rid;
    if (!nodeById.has(rid)) {
      nodeById.set(rid, {
        id: rid,
        label: rootName,
        hasHierarchicalChildren: true,
        numHierarchicalDescendants: 0,
        ...(rid.match(/^C\d+$/) ? { cui: rid } : {}),
      });
    }

    const subclasses = graph.subclasses ?? [];
    for (const item of subclasses) {
      const node = diseaseToNode(item.disease, rid);
      const diseaseId =
        node?.id ?? (item.disease.properties?.id != null ? String(item.disease.properties.id) : undefined);
      if (diseaseId) {
        const genes = (item.genes ?? [])
          .map((g: RawGeneNode) => rawGeneToGene(g))
          .filter((g): g is Gene => g != null);
        if (genes.length > 0) {
          const existing = genesByDiseaseId.get(diseaseId) ?? [];
          const seen = new Set(existing.map(gg => gg.id));
          for (const g of genes) {
            if (!seen.has(g.id)) {
              existing.push(g);
              seen.add(g.id);
            }
          }
          genesByDiseaseId.set(diseaseId, existing);
        }
      }
      if (node && !nodeById.has(node.id)) {
        nodeById.set(node.id, node);
      }
    }
  }

  if (rootId) {
    const rootNode = nodeById.get(rootId);
    if (rootNode) {
      const childCount = Array.from(nodeById.values()).filter(n => n.hierarchicalParent === rootId).length;
      rootNode.numHierarchicalDescendants = childCount;
      rootNode.hasHierarchicalChildren = childCount > 0;
    }
  }

  return { nodes: Array.from(nodeById.values()), genesByDiseaseId };
}

function normalizeNodesLegacy(rawNodes: RawNode[]): OntologyApiNode[] {
  const iriToId = new Map<string, string>();
  rawNodes.forEach(n => {
    const iri = getFirst(n.properties?.iri as string[] | string | undefined);
    if (iri) iriToId.set(iri, String(n.id));
  });

  return rawNodes.map(n => {
    const props = n.properties ?? {};
    const parentIri = getFirst(props.hierarchicalParent as string[] | undefined);
    const hierarchicalParent = parentIri ? iriToId.get(parentIri) : undefined;
    const label = getFirst(props[LABEL_KEY_LEGACY] as string[] | undefined) ?? `Node ${n.id}`;
    const hasHierarchicalChildren =
      getFirst(props.hasHierarchicalChildren as string[] | undefined) === 'true';
    const numStr = getFirst(props.numHierarchicalDescendants as string[] | undefined);
    const numHierarchicalDescendants = numStr ? Number(numStr) : 0;
    const cui = getFirst(props.UMLS_CUI as string[] | undefined);
    return {
      id: String(n.id),
      label,
      hierarchicalParent,
      hasHierarchicalChildren,
      numHierarchicalDescendants,
      ...(cui ? { cui } : {}),
    };
  });
}

async function loadMockGraph(): Promise<OntologyApiNode[]> {
  if (cachedNodes) return cachedNodes;

  const res = await fetch('/Response-graph.json');
  if (!res.ok) throw new Error('Failed to load ontology graph');
  const data: MockResponse = await res.json();
  const payload = data.payload;

  if (payload && 'records' in payload && Array.isArray((payload as RawPayload).records)) {
    const out = parseNewFormat(payload as RawPayload);
    cachedNodes = out.nodes;
    cachedGenesByDiseaseId = out.genesByDiseaseId;
  } else {
    const rawNodes =
      (payload as RawGraphPayloadLegacy)?.graph?.nodes ??
      (data as unknown as { graph?: { nodes?: RawNode[] } }).graph?.nodes ??
      [];
    cachedNodes = normalizeNodesLegacy(rawNodes);
    cachedGenesByDiseaseId = new Map();
  }

  return cachedNodes;
}

export async function getGenesForDisease(diseaseId: string): Promise<Gene[]> {
  await loadMockGraph();
  return cachedGenesByDiseaseId?.get(diseaseId) ?? [];
}

export async function getDiseasesForGene(geneId: string): Promise<{ id: string; label: string }[]> {
  await loadMockGraph();
  const result: { id: string; label: string }[] = [];
  if (!cachedGenesByDiseaseId || !cachedNodes) return result;
  for (const [diseaseId, genes] of cachedGenesByDiseaseId) {
    if (genes.some(g => g.id === geneId)) {
      const node = cachedNodes.find(n => n.id === diseaseId);
      result.push({ id: diseaseId, label: node?.label ?? diseaseId });
    }
  }
  return result;
}

export async function getOntologyNode(id: string): Promise<OntologyApiNode | null> {
  const nodes = await loadMockGraph();
  const byId = nodes.find(n => n.id === id);
  if (byId) return byId;
  return nodes.find(n => n.cui === id) ?? null;
}

export async function getOntologyChildren(id: string): Promise<OntologyApiNode[]> {
  const nodes = await loadMockGraph();
  return nodes.filter(n => n.hierarchicalParent === id);
}

export async function getExplorerRoots(limit = 10): Promise<OntologyApiNode[]> {
  const nodes = await loadMockGraph();
  const trueRoots = nodes.filter(n => !n.hierarchicalParent);
  if (trueRoots.length >= limit) return trueRoots.slice(0, limit);

  const firstRootId = trueRoots[0]?.id;
  const children = firstRootId ? nodes.filter(n => n.hierarchicalParent === firstRootId) : [];
  return [...trueRoots, ...children].slice(0, limit);
}

export type OntologyFullGraph = {
  nodes: OntologyApiNode[];
  edges: { source: string; target: string }[];
};

export async function getOntologyFullGraph(): Promise<OntologyFullGraph> {
  const nodes = await loadMockGraph();
  const edges = nodes
    .filter(n => n.hierarchicalParent != null)
    .map(n => ({ source: n.hierarchicalParent!, target: n.id }));
  return { nodes, edges };
}
